import { useGetMyConnections } from '@/hooks/api/dashboard'
import useAppStore from '@/store/useAppStore'
import { Box, InputAdornment, TextField } from '@mui/material'
import { useMemo, useState } from 'react'
import { shallow } from 'zustand/shallow'
import UserConnections from '../user-profile/details/Connections'
import { ConnectionsListContext } from '@/context/ConnectionsListContext'
import { useAuth } from '@/hooks/common'
import SVG from 'react-inlinesvg'
import { SVGS } from '@/assets/svgs'
import NoDataPlaceholder from '@/components/NoDataPlaceholder'
import { Page } from '../__styled'
import PageWrapper from '@/components/PageWrapper'

export default function MyConnectionsPage() {
  const [myConnections] = useAppStore((state) => [state.user.myConnections], shallow)
  const { getMyConnections, canLoadMore, loadMore, loadingNextPage } = useGetMyConnections()
  const { basicInfo } = useAuth()
  const connCount = basicInfo?.connCount || 0

  const [searchQuery, setSearchQuery] = useState('')

  const filteredConnections = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) return myConnections

    return myConnections.filter((connection) => {
      const firstName = connection.firstName?.toLowerCase() ?? ''
      const lastName = connection.lastName?.toLowerCase() ?? ''
      return firstName.includes(query) || lastName.includes(query)
    })
  }, [myConnections, searchQuery])

  const isSearching = searchQuery.trim().length > 0

  return (
    <PageWrapper
      title={`My Followers${connCount > 0 ? ` (${connCount})` : ''}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        width: '100%',
        overflow: 'auto',
      }}
    >
      {myConnections.length > 0 && (
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            backgroundColor: '#ffffff',
          }}
        >
          <Box sx={styles.searchContainer}>
            <TextField
              fullWidth
              placeholder='Search by name'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size='small'
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SVG
                      src={SVGS.SearchIcon}
                      style={{ color: '#9e9e9e', width: 20, height: 20 }}
                    />
                  </InputAdornment>
                ),
                sx: styles.searchInput,
              }}
            />
          </Box>
        </Box>
      )}

      <Box sx={styles.gridContainer}>
        {myConnections.length > 0 ? (
          filteredConnections.length > 0 ? (
            <ConnectionsListContext.Provider
              value={{
                items: filteredConnections,
                loading: getMyConnections.isLoading,
                loadingNextPage: isSearching ? false : loadingNextPage,
                canLoadNextPage: isSearching ? false : canLoadMore,
                loadNextPageData: loadMore,
                total: isSearching ? filteredConnections.length : Number(basicInfo.connCount),
              }}
            >
              <UserConnections onUserAction={() => {}} />
            </ConnectionsListContext.Provider>
          ) : (
            <Box
              display='flex'
              flexDirection={'column'}
              alignItems={'center'}
              sx={{
                display: 'flex',
                flex: 1,
                alignItems: 'center',
                justifyContent: 'center',
                pb: '200px',
                mt: '40px',
              }}
            >
              <NoDataPlaceholder
                icon={SVGS.NoConnectionsIcon}
                title={`No followers match "${searchQuery}"`}
                subTitle={''}
              />
            </Box>
          )
        ) : (
          myConnections.length == 0 &&
          !getMyConnections.isLoading && (
            <Box
              display='flex'
              flexDirection={'column'}
              alignItems={'center'}
              sx={{
                display: 'flex',
                flex: 1,
                alignItems: 'center',
                justifyContent: 'center',
                pb: '200px',
                mt: '40px',
              }}
            >
              <NoDataPlaceholder
                icon={SVGS.NoConnectionsIcon}
                title={`You don't have any followers yet!`}
                subTitle={''}
              />
            </Box>
          )
        )}
      </Box>
    </PageWrapper>
  )
}

const styles = {
  searchContainer: {
    px: '0px',
    pb: 2,
  },

  searchInput: {
    borderRadius: '12px',
    backgroundColor: '#f5f5f5',
    fontSize: '15px',
    height: '48px',
    color: '#1a1a1a',
    '& fieldset': {
      border: 'none',
    },
    '& input': {
      padding: '14px 12px',
      fontSize: '15px',
      '&::placeholder': {
        color: '#9e9e9e',
        opacity: 1,
      },
    },
    '& .MuiInputAdornment-root': {
      marginRight: '4px',
    },
  },

  gridContainer: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    width: '100%',
    flex: 1,
  },
}
